import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { loginAdmin, getAllAdmins, createAdmin, toggleAdminStatus, deleteAdmin, changePassword } from '../lib/auth';
import { Admin } from '../lib/auth';
import { Order, Product, Category } from '../types';
import ImageUploader from '../components/ImageUploader';
import logo from '../assets/logo-somzita.png';

type AdminView = 'orders' | 'products' | 'categories' | 'admins';

const STATUS_LABELS: Record<string,string> = { pending:'En attente', confirmed:'Confirmée', delivered:'Livrée', cancelled:'Annulée' };
const STATUS_COLORS: Record<string,string> = { pending:'#d4a857', confirmed:'#70c490', delivered:'#70c490', cancelled:'#d97070' };

const inp: React.CSSProperties = {
  width:'100%', background:'var(--surface2)', border:'1px solid var(--border)',
  color:'var(--text)', padding:'0.7rem 0.85rem', borderRadius:'10px',
  fontFamily:'Outfit,sans-serif', fontSize:'16px', outline:'none', WebkitAppearance:'none',
};
const lbl: React.CSSProperties = {
  display:'block', fontSize:'0.65rem', letterSpacing:'0.12em',
  textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.3rem',
};

const emptyForm = { name:'', short_description:'', long_description:'', price:'', stock:'0', min_quantity:'1', category_id:'', image_url:'' };

export default function AdminPage() {
  // ── Auth ──────────────────────────────────────────────────────────────
  const [currentAdmin, setCurrentAdmin] = useState<Admin | null>(null);
  const [loginForm,    setLoginForm]    = useState({ username:'', password:'' });
  const [loginError,   setLoginError]   = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // ── Navigation ────────────────────────────────────────────────────────
  const [view, setView] = useState<AdminView>('orders');

  // ── Data ──────────────────────────────────────────────────────────────
  const [orders,     setOrders]     = useState<Order[]>([]);
  const [products,   setProducts]   = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [admins,     setAdmins]     = useState<Admin[]>([]);

  // ── Product form ──────────────────────────────────────────────────────
  const [form,      setForm]      = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formMsg,   setFormMsg]   = useState('');

  // ── Category form ─────────────────────────────────────────────────────
  const [catName, setCatName] = useState('');
  const [catMsg,  setCatMsg]  = useState('');

  // ── Admin management form ─────────────────────────────────────────────
  const [newAdmin,    setNewAdmin]    = useState({ username:'', password:'', confirmPassword:'' });
  const [adminMsg,    setAdminMsg]    = useState('');
  const [changePwdId, setChangePwdId] = useState<number | null>(null);
  const [newPwd,      setNewPwd]      = useState('');
  const [pwdMsg,      setPwdMsg]      = useState('');

  // ── Load data ─────────────────────────────────────────────────────────
  const loadOrders = useCallback(async () => {
    const { data } = await supabase.from('orders').select('*, order_items(*, products(name))').order('created_at', { ascending: false }).limit(100);
    setOrders(data || []);
  }, []);

  const loadProducts = useCallback(async () => {
    const [{ data: prods }, { data: cats }] = await Promise.all([
      supabase.from('products').select('*, categories(name,slug)').eq('is_active', true).order('name'),
      supabase.from('categories').select('*').order('name'),
    ]);
    setProducts(prods || []);
    setCategories(cats || []);
  }, []);

  const loadAdmins = useCallback(async () => {
    const data = await getAllAdmins();
    setAdmins(data);
  }, []);

  useEffect(() => {
    if (!currentAdmin) return;
    loadOrders();
    loadProducts();
    if (currentAdmin.role === 'superadmin') loadAdmins();
  }, [currentAdmin, loadOrders, loadProducts, loadAdmins]);

  // ── Login ─────────────────────────────────────────────────────────────
  const handleLogin = async () => {
    if (!loginForm.username || !loginForm.password) return setLoginError('Remplissez tous les champs.');
    setLoginLoading(true); setLoginError('');
    const admin = await loginAdmin(loginForm.username, loginForm.password);
    if (admin) {
      setCurrentAdmin(admin);
      if (admin.role === 'superadmin') setView('admins');
    } else {
      setLoginError('Identifiants incorrects ou compte désactivé.');
    }
    setLoginLoading(false);
  };

  // ── Orders ────────────────────────────────────────────────────────────
  async function updateStatus(orderId: number, status: string) {
    await supabase.from('orders').update({ status }).eq('id', orderId);
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: status as any } : o));
  }

  // ── Products ──────────────────────────────────────────────────────────
  async function submitProduct(e: React.FormEvent) {
    e.preventDefault(); setFormMsg('');
    if (!form.name || !form.price) return setFormMsg('Nom et prix obligatoires.');
    const payload = {
      name: form.name, short_description: form.short_description || null,
      long_description: form.long_description || null, description: form.short_description || null,
      price: parseFloat(form.price), stock: parseInt(form.stock) || 0,
      min_quantity: parseInt(form.min_quantity) || 1,
      category_id: form.category_id ? parseInt(form.category_id) : null,
      image_url: form.image_url || null,
    };
    const { error } = editingId
      ? await supabase.from('products').update(payload).eq('id', editingId)
      : await supabase.from('products').insert(payload);
    if (error) return setFormMsg('Erreur : ' + error.message);
    setFormMsg(editingId ? '✅ Modifié !' : '✅ Ajouté !');
    setForm(emptyForm); setEditingId(null); loadProducts();
  }

  function startEdit(p: Product) {
    setEditingId(p.id);
    setForm({ name:p.name, short_description:p.short_description||'', long_description:p.long_description||p.description||'', price:String(p.price), stock:String(p.stock), min_quantity:String(p.min_quantity||1), category_id:p.category_id?String(p.category_id):'', image_url:p.image_url||'' });
    setFormMsg(''); setView('products');
    window.scrollTo({ top:0, behavior:'smooth' });
  }

  function cancelEdit() { setEditingId(null); setForm(emptyForm); setFormMsg(''); }

  async function deleteProduct(id: number) {
    if (!window.confirm('Supprimer ce produit ?')) return;
    await supabase.from('products').update({ is_active: false }).eq('id', id);
    loadProducts();
  }

  // ── Categories ────────────────────────────────────────────────────────
  async function addCategory(e: React.FormEvent) {
    e.preventDefault(); setCatMsg('');
    if (!catName.trim()) return setCatMsg('Nom obligatoire.');
    const slug = catName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'');
    const { error } = await supabase.from('categories').insert({ name: catName.trim(), slug });
    if (error) return setCatMsg('Erreur : ' + error.message);
    setCatMsg('✅ Ajoutée !'); setCatName(''); loadProducts();
  }

  async function deleteCat(id: number) {
    if (!window.confirm('Supprimer ?')) return;
    await supabase.from('categories').delete().eq('id', id);
    loadProducts();
  }

  // ── Admin management ──────────────────────────────────────────────────
  async function handleCreateAdmin(e: React.FormEvent) {
    e.preventDefault(); setAdminMsg('');
    if (!newAdmin.username || !newAdmin.password) return setAdminMsg('Remplissez tous les champs.');
    if (newAdmin.password !== newAdmin.confirmPassword) return setAdminMsg('Les mots de passe ne correspondent pas.');
    if (newAdmin.password.length < 6) return setAdminMsg('Mot de passe trop court (min. 6 caractères).');
    const result = await createAdmin(newAdmin.username, newAdmin.password, currentAdmin!.id);
    if (!result.success) return setAdminMsg('Erreur : ' + result.error);
    setAdminMsg('✅ Compte admin créé avec succès !');
    setNewAdmin({ username:'', password:'', confirmPassword:'' });
    loadAdmins();
  }

  async function handleToggle(id: number, isActive: boolean) {
    if (id === currentAdmin?.id) return;
    await toggleAdminStatus(id, !isActive);
    loadAdmins();
  }

  async function handleDelete(id: number) {
    if (id === currentAdmin?.id) return;
    if (!window.confirm('Supprimer ce compte admin ?')) return;
    await deleteAdmin(id);
    loadAdmins();
  }

  async function handleChangePwd(e: React.FormEvent) {
    e.preventDefault(); setPwdMsg('');
    if (!newPwd || newPwd.length < 6) return setPwdMsg('Min. 6 caractères.');
    const result = await changePassword(changePwdId!, newPwd);
    if (!result.success) return setPwdMsg('Erreur : ' + result.error);
    setPwdMsg('✅ Mot de passe modifié !');
    setNewPwd(''); setTimeout(() => { setChangePwdId(null); setPwdMsg(''); }, 1500);
  }

  const fmt = (n: number) => n.toLocaleString('fr-FR');
  const totalRevenue = orders.reduce((s,o) => s + Number(o.total), 0);
  const pending      = orders.filter(o => o.status === 'pending').length;

  // ── Nav items selon le rôle ───────────────────────────────────────────
  const navItems: { key: AdminView; icon: string; label: string }[] = [
    { key:'orders',     icon:'📦', label:'Commandes'  },
    { key:'products',   icon:'🛍️', label:'Produits'   },
    { key:'categories', icon:'🏷️', label:'Catégories' },
    ...(currentAdmin?.role === 'superadmin' ? [{ key:'admins' as AdminView, icon:'👥', label:'Admins' }] : []),
  ];

  // ══════════════════════════════════════════════════════════════════════
  // LOGIN SCREEN
  // ══════════════════════════════════════════════════════════════════════
  if (!currentAdmin) return (
    <div style={{ minHeight:'100dvh', display:'flex', alignItems:'center', justifyContent:'center', padding:'var(--page-px)' }}>
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'16px', padding:'2rem var(--page-px)', width:'min(380px,100%)', textAlign:'center' }}>
        <div style={{ display:'flex', justifyContent:'center', width:'100%', marginBottom:'0.8rem' }}>
          <img src={logo} alt="Somzita" style={{ height:'60px', width:'auto', objectFit:'contain' }} />
        </div>
        <div style={{ fontSize:'0.68rem', letterSpacing:'0.2em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'1.8rem' }}>Administration</div>

        <div style={{ textAlign:'left', display:'flex', flexDirection:'column', gap:'0.8rem' }}>
          <div>
            <label style={lbl}>Nom d'utilisateur</label>
            <input style={inp} type="text" value={loginForm.username}
              onChange={e => setLoginForm(f => ({ ...f, username: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
              placeholder="superadmin" autoCapitalize="none" />
          </div>
          <div>
            <label style={lbl}>Mot de passe</label>
            <input style={inp} type="password" value={loginForm.password}
              onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
              placeholder="••••••••" />
          </div>
          {loginError && (
            <p style={{ fontSize:'0.8rem', color:'var(--danger)', background:'rgba(217,112,112,0.08)', padding:'0.5rem 0.7rem', borderRadius:'8px', textAlign:'center' }}>
              {loginError}
            </p>
          )}
          <button onClick={handleLogin} disabled={loginLoading}
            style={{ height:'var(--touch-min)', background: loginLoading ? 'var(--muted)' : 'var(--accent)', color:'var(--bg)', border:'none', borderRadius:'2rem', fontSize:'0.9rem', fontWeight:500, marginTop:'0.3rem' }}>
            {loginLoading ? 'Connexion…' : 'Se connecter'}
          </button>
        </div>
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════
  // DASHBOARD
  // ══════════════════════════════════════════════════════════════════════
  return (
    <div style={{ minHeight:'100dvh', display:'flex', flexDirection:'column' }}>
      {/* Top bar */}
      <div style={{
        position:'sticky', top:0, zIndex:50, height:'52px', padding:'0 var(--page-px)',
        display:'flex', alignItems:'center', justifyContent:'space-between',
        background:'rgba(8,10,12,0.92)', backdropFilter:'blur(20px)', borderBottom:'1px solid var(--border)',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <img src={logo} alt="Somzita" style={{ height:'26px', width:'auto', objectFit:'contain' }} />
          <span style={{ fontSize:'0.6rem', color:'var(--muted)', letterSpacing:'0.12em', textTransform:'uppercase' }}>Admin</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.6rem' }}>
          {/* Badge rôle */}
          <span style={{
            fontSize:'0.62rem', padding:'0.15rem 0.6rem', borderRadius:'2rem',
            background: currentAdmin.role === 'superadmin' ? 'rgba(212,168,87,0.15)' : 'rgba(168,196,162,0.12)',
            color: currentAdmin.role === 'superadmin' ? 'var(--warning)' : 'var(--accent)',
            border: `1px solid ${currentAdmin.role === 'superadmin' ? 'rgba(212,168,87,0.3)' : 'rgba(168,196,162,0.25)'}`,
            letterSpacing:'0.08em', textTransform:'uppercase',
          }}>
            {currentAdmin.role === 'superadmin' ? '⭐ Super Admin' : '👤 ' + currentAdmin.username}
          </span>
          <button onClick={() => setCurrentAdmin(null)}
            style={{ background:'none', border:'none', color:'var(--danger)', fontSize:'0.8rem', height:'var(--touch-min)', padding:'0 0.4rem' }}>
            Déco.
          </button>
        </div>
      </div>

      {/* Content */}
      <main style={{ flex:1, padding:'1rem var(--page-px)', paddingBottom:'calc(70px + env(safe-area-inset-bottom))', overflowX:'hidden' }}>

        {/* ── ORDERS ── */}
        {view === 'orders' && (
          <>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.4rem', fontWeight:400, marginBottom:'1rem' }}>Commandes</h2>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'0.5rem', marginBottom:'1.2rem' }}>
              {[{l:'Total',v:orders.length},{l:'En attente',v:pending},{l:'CA FCFA',v:fmt(totalRevenue)}].map(s => (
                <div key={s.l} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'10px', padding:'0.7rem 0.6rem', textAlign:'center' }}>
                  <div style={{ fontSize:'0.58rem', letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.25rem' }}>{s.l}</div>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.3rem', color:'var(--accent2)' }}>{s.v}</div>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
              {orders.map(o => (
                <div key={o.id} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'10px', padding:'0.9rem' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.5rem' }}>
                    <div>
                      <span style={{ fontWeight:500, fontSize:'0.88rem' }}>{o.pseudo}</span>
                      <span style={{ color:'var(--muted)', fontSize:'0.72rem', marginLeft:'0.5rem' }}>#{o.id}</span>
                    </div>
                    <span style={{ fontFamily:"'Cormorant Garamond',serif", color:'var(--accent2)', fontSize:'1rem' }}>{fmt(Number(o.total))} F</span>
                  </div>
                  <div style={{ fontSize:'0.75rem', color:'var(--muted)', marginBottom:'0.3rem' }}>📞 {o.phone}</div>
                  <div style={{ fontSize:'0.75rem', color:'var(--muted)', marginBottom:'0.3rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>📍 {o.address}</div>
                  {o.latitude && o.longitude && (
                    <a href={`https://maps.google.com/?q=${o.latitude},${o.longitude}`} target="_blank" rel="noreferrer"
                      style={{ display:'inline-block', fontSize:'0.72rem', color:'var(--accent)', marginBottom:'0.4rem' }}>
                      🗺️ Voir sur Maps
                    </a>
                  )}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:'0.4rem' }}>
                    <span style={{ fontSize:'0.68rem', color:'var(--muted)' }}>{new Date(o.created_at).toLocaleString('fr-FR')}</span>
                    <select value={o.status} onChange={e => updateStatus(o.id, e.target.value)}
                      style={{ background:'var(--surface2)', border:'1px solid var(--border)', color:STATUS_COLORS[o.status]||'var(--text)', padding:'0.25rem 0.5rem', borderRadius:'6px', fontSize:'0.75rem', outline:'none', WebkitAppearance:'none' }}>
                      {Object.entries(STATUS_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                </div>
              ))}
              {orders.length === 0 && <p style={{ textAlign:'center', color:'var(--muted)', padding:'2rem', fontSize:'0.85rem' }}>Aucune commande.</p>}
            </div>
          </>
        )}

        {/* ── PRODUCTS ── */}
        {view === 'products' && (
          <>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.4rem', fontWeight:400, marginBottom:'1rem' }}>
              {editingId ? '✏️ Modifier' : 'Produits'}
            </h2>
            <div style={{ background:'var(--surface)', border:`1px solid ${editingId ? 'rgba(168,196,162,0.3)' : 'var(--border)'}`, borderRadius:'12px', padding:'1rem', marginBottom:'1.2rem' }}>
              <p style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:400, fontSize:'1rem', marginBottom:'0.9rem' }}>
                {editingId ? 'Modifier le produit' : 'Ajouter un produit'}
              </p>
              <form onSubmit={submitProduct} style={{ display:'flex', flexDirection:'column', gap:'0.7rem' }}>
                <div>
                  <label style={lbl}>Nom *</label>
                  <input style={inp} value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} placeholder="Nom du produit" />
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.6rem' }}>
                  <div>
                    <label style={lbl}>Prix (FCFA) *</label>
                    <input style={inp} type="number" min="0" step="50" value={form.price} onChange={e => setForm(f=>({...f,price:e.target.value}))} placeholder="500" />
                  </div>
                  <div>
                    <label style={lbl}>Stock</label>
                    <input style={inp} type="number" min="0" value={form.stock} onChange={e => setForm(f=>({...f,stock:e.target.value}))} />
                  </div>
                  <div>
                    <label style={lbl}>Qté min.</label>
                    <input style={inp} type="number" min="1" value={form.min_quantity} onChange={e => setForm(f=>({...f,min_quantity:e.target.value}))} placeholder="1" />
                  </div>
                </div>
                <div>
                  <label style={lbl}>Catégorie</label>
                  <select style={inp} value={form.category_id} onChange={e => setForm(f=>({...f,category_id:e.target.value}))}>
                    <option value="">— Aucune —</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Image</label>
                  <ImageUploader currentUrl={form.image_url} onUploaded={url => setForm(f => ({...f, image_url: url}))} />
                </div>
                <div>
                  <label style={lbl}>Courte description <span style={{ color:'var(--accent)', opacity:0.8 }}>(vitrine)</span></label>
                  <input style={inp} value={form.short_description} onChange={e => setForm(f=>({...f,short_description:e.target.value}))} placeholder="Ex: Protection optimale" maxLength={120} />
                  <span style={{ fontSize:'0.65rem', color:'var(--muted)' }}>{form.short_description.length}/120</span>
                </div>
                <div>
                  <label style={lbl}>Longue description <span style={{ color:'var(--accent)', opacity:0.8 }}>(fiche produit)</span></label>
                  <textarea style={{ ...inp, resize:'vertical' }} rows={4} value={form.long_description} onChange={e => setForm(f=>({...f,long_description:e.target.value}))} placeholder="Description complète…" />
                </div>
                {formMsg && <div style={{ fontSize:'0.8rem', color: formMsg.startsWith('✅') ? 'var(--success)' : 'var(--danger)', background: formMsg.startsWith('✅') ? 'rgba(112,196,144,0.08)' : 'rgba(217,112,112,0.08)', padding:'0.5rem 0.7rem', borderRadius:'8px' }}>{formMsg}</div>}
                <div style={{ display:'flex', gap:'0.5rem' }}>
                  <button type="submit" style={{ flex:1, height:'var(--touch-min)', background:'var(--accent)', color:'var(--bg)', border:'none', borderRadius:'2rem', fontSize:'0.88rem', fontWeight:500 }}>
                    {editingId ? '✓ Enregistrer' : 'Ajouter'}
                  </button>
                  {editingId && (
                    <button type="button" onClick={cancelEdit} style={{ height:'var(--touch-min)', padding:'0 1rem', background:'none', border:'1px solid var(--border)', color:'var(--muted)', borderRadius:'2rem', fontSize:'0.85rem' }}>
                      Annuler
                    </button>
                  )}
                </div>
              </form>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
              {products.map(p => (
                <div key={p.id} style={{ background:'var(--surface)', border:`1px solid ${editingId===p.id ? 'rgba(168,196,162,0.3)' : 'var(--border)'}`, borderRadius:'10px', padding:'0.75rem', display:'flex', alignItems:'center', gap:'0.75rem' }}>
                  <div style={{ width:'3rem', height:'3rem', borderRadius:'8px', overflow:'hidden', background:'var(--surface2)', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.4rem' }}>
                    {p.image_url ? <img src={p.image_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : '📦'}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:'0.85rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</div>
                    <div style={{ fontSize:'0.72rem', color:'var(--accent2)' }}>{fmt(Number(p.price))} FCFA · stock: {p.stock} · min: {p.min_quantity}</div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.3rem', flexShrink:0 }}>
                    <button onClick={() => startEdit(p)} style={{ height:'32px', padding:'0 0.7rem', background:'rgba(168,196,162,0.1)', border:'1px solid rgba(168,196,162,0.25)', color:'var(--accent)', borderRadius:'6px', fontSize:'0.72rem' }}>✏️</button>
                    <button onClick={() => deleteProduct(p.id)} style={{ height:'32px', padding:'0 0.7rem', background:'rgba(217,112,112,0.1)', border:'1px solid rgba(217,112,112,0.25)', color:'var(--danger)', borderRadius:'6px', fontSize:'0.72rem' }}>🗑️</button>
                  </div>
                </div>
              ))}
              {products.length === 0 && <p style={{ textAlign:'center', color:'var(--muted)', padding:'2rem', fontSize:'0.85rem' }}>Aucun produit.</p>}
            </div>
          </>
        )}

        {/* ── CATEGORIES ── */}
        {view === 'categories' && (
          <>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.4rem', fontWeight:400, marginBottom:'1rem' }}>Catégories</h2>
            <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'12px', padding:'1rem', marginBottom:'1.2rem' }}>
              <form onSubmit={addCategory} style={{ display:'flex', flexDirection:'column', gap:'0.7rem' }}>
                <div>
                  <label style={lbl}>Nouvelle catégorie</label>
                  <input style={inp} value={catName} onChange={e => setCatName(e.target.value)} placeholder="Ex: Lubrifiants" />
                </div>
                {catMsg && <div style={{ fontSize:'0.8rem', color: catMsg.startsWith('✅') ? 'var(--success)' : 'var(--danger)' }}>{catMsg}</div>}
                <button type="submit" style={{ height:'var(--touch-min)', background:'var(--accent)', color:'var(--bg)', border:'none', borderRadius:'2rem', fontSize:'0.88rem', fontWeight:500 }}>Ajouter</button>
              </form>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
              {categories.map(c => (
                <div key={c.id} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'10px', padding:'0.75rem 0.9rem', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div>
                    <div style={{ fontSize:'0.85rem' }}>{c.name}</div>
                    <div style={{ fontSize:'0.68rem', color:'var(--muted)', fontFamily:'monospace' }}>{c.slug}</div>
                  </div>
                  <button onClick={() => deleteCat(c.id)} style={{ height:'32px', padding:'0 0.7rem', background:'rgba(217,112,112,0.1)', border:'1px solid rgba(217,112,112,0.25)', color:'var(--danger)', borderRadius:'6px', fontSize:'0.72rem' }}>🗑️</button>
                </div>
              ))}
              {categories.length === 0 && <p style={{ textAlign:'center', color:'var(--muted)', padding:'2rem', fontSize:'0.85rem' }}>Aucune catégorie.</p>}
            </div>
          </>
        )}

        {/* ── ADMINS (Super Admin seulement) ── */}
        {view === 'admins' && currentAdmin.role === 'superadmin' && (
          <>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.4rem', fontWeight:400, marginBottom:'0.3rem' }}>Gestion des admins</h2>
            <p style={{ fontSize:'0.75rem', color:'var(--muted)', marginBottom:'1.2rem' }}>Seul le Super Admin peut créer et gérer les comptes administrateurs.</p>

            {/* Créer un admin */}
            <div style={{ background:'var(--surface)', border:'1px solid rgba(168,196,162,0.2)', borderRadius:'12px', padding:'1rem', marginBottom:'1.5rem' }}>
              <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1rem', marginBottom:'0.9rem' }}>Créer un nouveau compte admin</p>
              <form onSubmit={handleCreateAdmin} style={{ display:'flex', flexDirection:'column', gap:'0.7rem' }}>
                <div>
                  <label style={lbl}>Nom d'utilisateur</label>
                  <input style={inp} type="text" value={newAdmin.username}
                    onChange={e => setNewAdmin(f => ({ ...f, username: e.target.value }))}
                    placeholder="Ex: admin_ouaga" autoCapitalize="none" />
                </div>
                <div>
                  <label style={lbl}>Mot de passe</label>
                  <input style={inp} type="password" value={newAdmin.password}
                    onChange={e => setNewAdmin(f => ({ ...f, password: e.target.value }))}
                    placeholder="Min. 6 caractères" />
                </div>
                <div>
                  <label style={lbl}>Confirmer le mot de passe</label>
                  <input style={inp} type="password" value={newAdmin.confirmPassword}
                    onChange={e => setNewAdmin(f => ({ ...f, confirmPassword: e.target.value }))}
                    placeholder="Répétez le mot de passe" />
                </div>
                {adminMsg && (
                  <div style={{ fontSize:'0.8rem', color: adminMsg.startsWith('✅') ? 'var(--success)' : 'var(--danger)', background: adminMsg.startsWith('✅') ? 'rgba(112,196,144,0.08)' : 'rgba(217,112,112,0.08)', padding:'0.5rem 0.7rem', borderRadius:'8px' }}>
                    {adminMsg}
                  </div>
                )}
                <button type="submit" style={{ height:'var(--touch-min)', background:'var(--accent)', color:'var(--bg)', border:'none', borderRadius:'2rem', fontSize:'0.88rem', fontWeight:500 }}>
                  Créer le compte
                </button>
              </form>
            </div>

            {/* Liste des admins */}
            <h3 style={{ fontSize:'0.8rem', letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.7rem' }}>
              Comptes existants ({admins.length})
            </h3>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
              {admins.map(a => (
                <div key={a.id} style={{
                  background:'var(--surface)', border:`1px solid ${a.id === currentAdmin.id ? 'rgba(168,196,162,0.3)' : 'var(--border)'}`,
                  borderRadius:'10px', padding:'0.85rem',
                }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.5rem' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                      <span style={{ fontSize:'0.88rem', fontWeight:500 }}>{a.username}</span>
                      {a.id === currentAdmin.id && (
                        <span style={{ fontSize:'0.6rem', color:'var(--accent)', background:'rgba(168,196,162,0.1)', border:'1px solid rgba(168,196,162,0.25)', padding:'0.1rem 0.4rem', borderRadius:'2rem' }}>Vous</span>
                      )}
                    </div>
                    <span style={{
                      fontSize:'0.62rem', padding:'0.15rem 0.55rem', borderRadius:'2rem',
                      background: a.role === 'superadmin' ? 'rgba(212,168,87,0.15)' : 'rgba(168,196,162,0.1)',
                      color: a.role === 'superadmin' ? 'var(--warning)' : 'var(--accent)',
                      border: `1px solid ${a.role === 'superadmin' ? 'rgba(212,168,87,0.3)' : 'rgba(168,196,162,0.2)'}`,
                    }}>
                      {a.role === 'superadmin' ? '⭐ Super Admin' : '👤 Admin'}
                    </span>
                  </div>

                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <span style={{ fontSize:'0.68rem', color: a.is_active ? 'var(--success)' : 'var(--danger)' }}>
                      {a.is_active ? '● Actif' : '● Désactivé'}
                    </span>
                    <span style={{ fontSize:'0.65rem', color:'var(--muted)' }}>
                      {new Date(a.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>

                  {/* Actions — pas sur soi-même ni sur superadmin */}
                  {a.id !== currentAdmin.id && a.role !== 'superadmin' && (
                    <div style={{ display:'flex', gap:'0.4rem', marginTop:'0.7rem', flexWrap:'wrap' }}>
                      <button onClick={() => handleToggle(a.id, a.is_active)}
                        style={{ height:'32px', padding:'0 0.7rem', background: a.is_active ? 'rgba(212,168,87,0.1)' : 'rgba(112,196,144,0.1)', border:`1px solid ${a.is_active ? 'rgba(212,168,87,0.3)' : 'rgba(112,196,144,0.3)'}`, color: a.is_active ? 'var(--warning)' : 'var(--success)', borderRadius:'6px', fontSize:'0.72rem' }}>
                        {a.is_active ? '⏸ Désactiver' : '▶ Activer'}
                      </button>
                      <button onClick={() => { setChangePwdId(a.id); setNewPwd(''); setPwdMsg(''); }}
                        style={{ height:'32px', padding:'0 0.7rem', background:'rgba(168,196,162,0.1)', border:'1px solid rgba(168,196,162,0.25)', color:'var(--accent)', borderRadius:'6px', fontSize:'0.72rem' }}>
                        🔑 Mot de passe
                      </button>
                      <button onClick={() => handleDelete(a.id)}
                        style={{ height:'32px', padding:'0 0.7rem', background:'rgba(217,112,112,0.1)', border:'1px solid rgba(217,112,112,0.25)', color:'var(--danger)', borderRadius:'6px', fontSize:'0.72rem' }}>
                        🗑️ Supprimer
                      </button>
                    </div>
                  )}

                  {/* Formulaire changement de mot de passe inline */}
                  {changePwdId === a.id && (
                    <form onSubmit={handleChangePwd} style={{ marginTop:'0.7rem', display:'flex', flexDirection:'column', gap:'0.5rem', background:'var(--surface2)', padding:'0.7rem', borderRadius:'8px' }}>
                      <label style={lbl}>Nouveau mot de passe pour {a.username}</label>
                      <input style={inp} type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder="Min. 6 caractères" />
                      {pwdMsg && <div style={{ fontSize:'0.75rem', color: pwdMsg.startsWith('✅') ? 'var(--success)' : 'var(--danger)' }}>{pwdMsg}</div>}
                      <div style={{ display:'flex', gap:'0.4rem' }}>
                        <button type="submit" style={{ flex:1, height:'36px', background:'var(--accent)', color:'var(--bg)', border:'none', borderRadius:'6px', fontSize:'0.8rem', fontWeight:500 }}>Confirmer</button>
                        <button type="button" onClick={() => setChangePwdId(null)} style={{ height:'36px', padding:'0 0.8rem', background:'none', border:'1px solid var(--border)', color:'var(--muted)', borderRadius:'6px', fontSize:'0.8rem' }}>Annuler</button>
                      </div>
                    </form>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      {/* Bottom nav */}
      <nav style={{
        position:'fixed', bottom:0, left:0, right:0, zIndex:50,
        height:'calc(56px + env(safe-area-inset-bottom))',
        paddingBottom:'env(safe-area-inset-bottom)',
        background:'rgba(16,19,24,0.95)', backdropFilter:'blur(20px)',
        borderTop:'1px solid var(--border)',
        display:'flex', alignItems:'stretch',
      }}>
        {navItems.map(n => (
          <button key={n.key} onClick={() => { setView(n.key); cancelEdit(); }}
            style={{
              flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'0.2rem',
              background:'none', border:'none',
              color: view === n.key ? 'var(--accent)' : 'var(--muted)',
              fontSize:'0.62rem', letterSpacing:'0.05em',
              borderTop: view === n.key ? '2px solid var(--accent)' : '2px solid transparent',
              transition:'color 0.2s',
            }}>
            <span style={{ fontSize:'1.2rem' }}>{n.icon}</span>
            {n.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
